import { useEffect, useState, useRef } from 'react';
import { withRouter, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { DESCENDING } from 'constants/sortDirections';
import { ButtonGroup, GroupButton } from 'ui/buttonGroup';
import { Table } from 'ui/table/Table';
import { Menu } from 'ui/menu';
import { Tooltip } from 'ui/tooltip';
import { IconButton, BUTTON_VARIANTS } from 'ui/button';
import { BasicFilterSelect, MultiFilterSelect } from 'ui/selects';
import { colors, spacing } from 'ui/theme';

import { getUrlParams } from 'utils/urlParams';
import { ALIGNMENT } from 'ui/menu/constants';

import { Overflow, WarningFill } from 'ui/icons';
import { isEmpty } from '@iterable/utils';
import { fetchUserMappingsRequest } from 'modules/analytics/actions';
import { selectUserMappings } from 'modules/analytics/selectors';
import {
  selectIsExportUrlFiltersCAFeatureFlagOn,
  selectIsSegmentAnalysisEmailOpenRateFeatureFlagOn,
  selectIsSegmentCardinalityImprovementsFeatureFlagOn,
} from 'core/userContext/selectors';
import { useSegmentSortByParam } from 'modules/productAnalytics/hooks';
import { SEGMENT_CARDINALITY_LIMIT } from 'modules/productAnalytics/constants';
import {
  parseUserMappingsForDropdown,
  parseColsToCSVHeaders,
  metricSentenceCase,
  sortBySelectionOrder,
} from 'modules/productAnalytics/utils';
import {
  HiddenCSVLink,
  StyledDivider,
  TooltipCardinalityTrigger,
} from 'modules/productAnalytics/Styled';
import { fetchSegmentsV2Request } from 'modules/messagingInsights/actions';
import {
  CampaignMetricLabels,
  OpenMetrics,
  FETCH_ERRORS as e,
} from 'modules/messagingInsights/constants';
import {
  selectSegmentData,
  selectSegmentsLoading,
  selectError,
} from 'modules/messagingInsights/selectors';
import { Segment } from 'modules/messagingInsights/assets/segment';
import { sortRows } from 'ui/table/utils';
import {
  NoSegmentSelectedMessage,
  SelectSegmentMessage,
  NoMatchingSegmentsMessage,
  CheckFiltersMessage,
} from 'constants/messageCopies';
import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';

import { LayoutTemplate } from './LayoutTemplate';
import {
  StyledHeader,
  StyledRows,
  TableWrapper,
  StyledCampaignAnalyticsCard,
  FilterCard,
  StyledSelectLabel,
  SegmentSelector,
  SegmentMetricSelector,
  SelectWrapper,
  CampaignAnalyticsCardHeader,
  StyledInfoBanner,
  StyledWarningBanner,
} from './Styled';
import {
  getSegmentCols,
  getMetricArray,
  getMetricColumns,
  getOrderedSelection,
  getSegmentVals,
} from '../utils';
import {
  selectMessageMedium,
  selectIsCustomConversion,
  selectSelectedVariant,
  selectAnalyticsMetricsOptions,
  selectAnalyticsMetricsValueTypes,
} from '../selectors';
import { loadingCols, loadingRows, TIME_SERIES_AND_SEGMENTS_DEFAULT_METRICS } from '../constants';
import { NoDataCard } from './NoDataCard';
import { useFiltersContext, useTimeFiltersContext } from '../context';

const SEGMENT_LIMIT_ERROR =
  'Search results exceed limit. Please refine your search criteria and try again.';
const copy = {
  limitError: {
    title: 'Search results exceed limit',
    subtitle: 'Please refine your search criteria and try again.',
  },
  fetchError: {
    title: "Sorry we couldn't load this page",
    subtitle: 'Try refreshing the page. If this keeps happening, let us know.',
  },
};

const StyledWarningIcon = styled(WarningFill)`
  margin-right: ${spacing.quarter};
`;

export const CampaignAnalyticsSegments = () => {
  const csvLink = useRef();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const { campaignId } = getUrlParams(search);
  const { segment, setSegment, segmentMetrics: metrics, setSegmentMetrics } = useFiltersContext();
  const { dateParams } = useTimeFiltersContext();
  const AnalyticsMetricsOptions = useSelector(selectAnalyticsMetricsOptions) || [];

  const isSegAnalysisEmailOpenRateFFOn = useSelector(
    selectIsSegmentAnalysisEmailOpenRateFeatureFlagOn,
  );
  const isSegCardinalityFFOn = useSelector(selectIsSegmentCardinalityImprovementsFeatureFlagOn);
  const exportUrlFiltersCAFFOn = useSelector(selectIsExportUrlFiltersCAFeatureFlagOn);
  const isCustomConversion = useSelector(selectIsCustomConversion);
  const userMappings = useSelector(selectUserMappings);
  const areSegmentsLoading = useSelector(selectSegmentsLoading);
  const messageMedium = useSelector(selectMessageMedium);
  const allSegments = userMappings && parseUserMappingsForDropdown(userMappings);
  const selectedVariant = useSelector(selectSelectedVariant);
  const { templateId } = selectedVariant;
  const [isSegmentDate, setIsSegmentDate] = useState(false);
  const [segmentInverval, setSegmentInterval] = useState('1M');
  const [segmentSortedBy, setSegmentSortedBy] = useState('Segment');
  const [segmentSortDirection, setSegmentSortDirection] = useState(DESCENDING);
  const isHighCardinality = isSegCardinalityFFOn && segment?.name === 'domain';
  const segmentData = useSelector(selectSegmentData) || { UnsupportedMetrics: [] };
  const parsedSegmentData = getSegmentVals(segmentData, segment?.type);
  const segmentCols = getSegmentCols(segment?.name, segment?.type === 'date');

  const errors = useSelector(selectError) || {};
  const showErrorState = Object.keys(errors).includes(e.FETCH_SEGMENTSV2);
  const isLimitError = errors[e.FETCH_SEGMENTSV2] === SEGMENT_LIMIT_ERROR;

  const valueTypes = useSelector(selectAnalyticsMetricsValueTypes);

  // If the campaign does not have custom conversion event, exclude the conversion metrics
  const allMetrics = AnalyticsMetricsOptions?.filter(
    (el) =>
      isCustomConversion || (!isCustomConversion && !el.label.toLowerCase().includes('conversion')),
  ).map((metric) => ({ ...metric, label: metricSentenceCase(metric.label) }));
  const metricsInOrder = metrics?.map((el) => METRIC_VALUES_TO_LABELS[el]);
  const metricArray = getOrderedSelection(metricsInOrder, getMetricArray(parsedSegmentData));
  segmentCols.push(...getMetricColumns(metricArray, valueTypes, true));

  const overflowActions = {
    'export-segment-table': () => csvLink.current.link.click(),
  };
  const isInitialMount = useRef(true);

  // Unsupported metrics and metric exceptions (Email Open Rate) banner logic
  const { UnsupportedMetrics } = segmentData;
  const selectedUnsupportedMetrics = metrics
    ?.map((metric) => METRIC_VALUES_TO_LABELS[metric])
    .filter((metricName) => UnsupportedMetrics.includes(metricName));
  const supportedMetrics = metrics.filter(
    (metric) => !(UnsupportedMetrics || []).includes(METRIC_VALUES_TO_LABELS[metric]),
  );
  const showMetricDefWarning = isSegAnalysisEmailOpenRateFFOn && metrics.includes('EmailOpenRate');
  const showUnsupportedMetricWarning = selectedUnsupportedMetrics?.length > 0;
  const showMetricsBanner = segment && (showUnsupportedMetricWarning || showMetricDefWarning);

  const sortByParam = useSegmentSortByParam(
    segmentSortedBy,
    supportedMetrics[0],
    AnalyticsMetricsOptions,
  );
  const sortByParamVal =
    METRIC_VALUES_TO_LABELS?.[sortByParam.sortByMediumMetric?.name || ''] || '';

  const [cardinalitySortBy, setCardinalitySortBy] = useState(sortByParamVal);
  const displayedSortedBy = isHighCardinality ? cardinalitySortBy : segmentSortedBy;
  const sortedSegmentRows = sortRows(
    parsedSegmentData,
    displayedSortedBy,
    segmentSortDirection,
  ).filter((row) => (isHighCardinality ? row?.Segment !== 'Not Included In Results' : true));
  const segmentCount = sortedSegmentRows?.length || 0;
  const showHighCardinalityFeedback = isHighCardinality && segmentCount > SEGMENT_CARDINALITY_LIMIT;

  const highCardinalityColumns = segmentCols?.map((col) => {
    return {
      ...col,
      name:
        col.value === cardinalitySortBy &&
        col.value !== 'Segment' &&
        showHighCardinalityFeedback ? (
          <Tooltip tooltipContent="Only sorting based on the first 250 records">
            <TooltipCardinalityTrigger>
              <StyledWarningIcon color={colors.purple90} /> {col.name}
            </TooltipCardinalityTrigger>
          </Tooltip>
        ) : (
          col.name
        ),
    };
  });

  useEffect(() => {
    setCardinalitySortBy(sortByParamVal);
  }, [segment]);

  useEffect(() => {
    if (!userMappings.length) {
      dispatch(fetchUserMappingsRequest());
    }
  }, []);

  useEffect(() => {
    if (messageMedium && !metrics.length) {
      const defaultSelectedMetrics = TIME_SERIES_AND_SEGMENTS_DEFAULT_METRICS[messageMedium];
      setSegmentMetrics(defaultSelectedMetrics);
    }
  }, [messageMedium]);

  /*
      Given a campaignId, this effect retrieves the segment data.
      Effect is triggered when the date, selected segment, selected metric, or segment interval is updated.
  */
  useEffect(() => {
    if (campaignId && !isEmpty(dateParams)) {
      setCardinalitySortBy(sortByParamVal);

      const queryData = {
        campaignIds: [parseInt(campaignId, 10)],
        endTime: dateParams.endTime,
        mediums: [messageMedium],
        mediumMetrics: metrics.map((el) => ({ name: el })),
        startTime: dateParams.startTime,
        userProperty: segment?.name,
        templateId,
        showUnsupportedMetrics: true,
        ...(isSegCardinalityFFOn ? sortByParam : {}),
      };
      if (segment?.type === 'date') {
        queryData.interval = segmentInverval;
        setIsSegmentDate(true);
      } else {
        setIsSegmentDate(false);
      }

      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else if (segment?.name) {
        dispatch(fetchSegmentsV2Request(queryData));
      }
    }
  }, [segment, segmentInverval, metrics, dateParams, templateId]);

  useEffect(() => {
    if (isHighCardinality && segmentSortedBy !== 'Segment' && campaignId && !isEmpty(dateParams)) {
      const queryData = {
        campaignIds: [parseInt(campaignId, 10)],
        endTime: dateParams.endTime,
        mediums: [messageMedium],
        mediumMetrics: metrics.map((el) => ({ name: el })),
        startTime: dateParams.startTime,
        userProperty: segment?.name,
        templateId,
        showUnsupportedMetrics: true,
        ...sortByParam,
      };
      if (segment?.type === 'date') {
        queryData.interval = segmentInverval;
        setIsSegmentDate(true);
      } else {
        setIsSegmentDate(false);
      }

      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else if (segment?.name) {
        dispatch(fetchSegmentsV2Request(queryData));
      }
    }
  }, [segmentSortedBy]);

  useEffect(() => {
    // set type for url-provided segments
    if (exportUrlFiltersCAFFOn && !segment?.type) {
      const matchingSegment = allSegments.filter(
        (opt) => opt.value?.name?.toLocaleLowerCase() === segment?.name?.toLocaleLowerCase(),
      )[0];
      const type = matchingSegment?.value?.type;
      if (type) {
        setSegment(matchingSegment?.value || { ...segment, type });
      }
    }
  }, [segment, allSegments]);

  return (
    <LayoutTemplate
      pageName="Segment analysis"
      canShowFPOBanner={metrics?.some((metric) => OpenMetrics.includes(metric))}
    >
      <FilterCard>
        {userMappings.length > 0 && (
          <SegmentSelector>
            <StyledSelectLabel>Select a segment</StyledSelectLabel>
            <SelectWrapper>
              <BasicFilterSelect
                onSelect={(vals) => {
                  const val = vals[0] || {};
                  setSegment(val);
                }}
                placeholder={segment?.name || 'Select a segment to analyze...'}
                id="user-mappings"
                title="user-mappings"
                options={allSegments}
                isFullWidth
              />
            </SelectWrapper>
          </SegmentSelector>
        )}
        {AnalyticsMetricsOptions.length > 0 && (
          <SegmentMetricSelector>
            <StyledSelectLabel>Select metrics</StyledSelectLabel>
            <SelectWrapper>
              <MultiFilterSelect
                content={{
                  clearText: 'Clear',
                  selectNone: 'Select none',
                  selectAll: 'Select all',
                }}
                defaultValues={metrics}
                placeholder={'0 types of metrics'}
                inputContent={{ filterPlaceholder: 'Search all metrics' }}
                displayContent={{
                  multipleSelectedText: 'types of metrics',
                }}
                onSelect={(val) => {
                  const sortedVal = sortBySelectionOrder(metrics || [], val);
                  setSegmentMetrics(sortedVal);
                }}
                id={`campaign-metrics`}
                title={`campaign-metrics`}
                options={allMetrics}
                categoryLabels={CampaignMetricLabels}
              />
            </SelectWrapper>
          </SegmentMetricSelector>
        )}
        {isSegmentDate && (
          <div>
            <ButtonGroup defaultSelectedId={`opt-1M`} trackSelected>
              <GroupButton key={`opt-1w`} id={`opt-1w`} onSelect={() => setSegmentInterval('1w')}>
                Weekly
              </GroupButton>
              <GroupButton key={`opt-1M`} id={`opt-1M`} onSelect={() => setSegmentInterval('1M')}>
                Monthly
              </GroupButton>
              <GroupButton key={`opt-1q`} id={`opt-1q`} onSelect={() => setSegmentInterval('1q')}>
                Quarterly
              </GroupButton>
            </ButtonGroup>
          </div>
        )}
      </FilterCard>
      {showMetricsBanner && (
        <StyledInfoBanner>
          <>
            {showUnsupportedMetricWarning &&
              `${selectedUnsupportedMetrics.length}
          of the metrics you have selected cannot be displayed in the Segments table:
          ${selectedUnsupportedMetrics.join(', ')}.`}
            {showUnsupportedMetricWarning && showMetricDefWarning && <br />}
            {showMetricDefWarning &&
              'For segment data, Email Open Rate is calculated as Email Opens (one per messageId) / (Total Emails Delivered).'}
          </>
        </StyledInfoBanner>
      )}
      {showHighCardinalityFeedback && (
        <StyledWarningBanner>
          The <b>{segment?.name}</b> segment can only display the first 250 records.
        </StyledWarningBanner>
      )}
      <StyledCampaignAnalyticsCard>
        <CampaignAnalyticsCardHeader>
          Analysis
          <Menu
            handleSelectionChange={(id) => id && overflowActions[id]()}
            horizontalAlignment={ALIGNMENT.RIGHT}
            options={[{ label: 'Export table to csv', id: 'export-segment-table' }]}
          >
            <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
              <Overflow />
            </IconButton>
          </Menu>
          <HiddenCSVLink
            data={sortedSegmentRows}
            headers={parseColsToCSVHeaders(segmentCols)}
            filename={'segment_table.csv'}
            ref={csvLink}
            target="_blank"
          />
        </CampaignAnalyticsCardHeader>
        <StyledDivider />
        {areSegmentsLoading ? (
          <TableWrapper>
            <Table>
              <StyledHeader columns={loadingCols} isSticky />
              <StyledRows rows={loadingRows} idKey="loading" columns={loadingCols} />
            </Table>
          </TableWrapper>
        ) : (
          <>
            {showErrorState && (
              <NoDataCard
                Icon={Segment}
                title={isLimitError ? copy.limitError.title : copy.fetchError.title}
                subtitle={isLimitError ? copy.limitError.subtitle : copy.fetchError.subtitle}
              />
            )}
            {!showErrorState &&
              (sortedSegmentRows.length > 0 ? (
                <TableWrapper>
                  <Table>
                    <StyledHeader
                      columns={highCardinalityColumns}
                      sortedBy={displayedSortedBy}
                      setSortedBy={(sort) => {
                        setSegmentSortedBy(sort);
                        setCardinalitySortBy(sort);
                      }}
                      sortDirection={segmentSortDirection}
                      setSortDirection={setSegmentSortDirection}
                      isSticky
                    />
                    <StyledRows rows={sortedSegmentRows} idKey="Segment" columns={segmentCols} />
                  </Table>
                </TableWrapper>
              ) : (
                <NoDataCard
                  Icon={Segment}
                  title={!segment?.name ? NoSegmentSelectedMessage : NoMatchingSegmentsMessage}
                  subtitle={!segment?.name ? SelectSegmentMessage : CheckFiltersMessage}
                />
              ))}
          </>
        )}
      </StyledCampaignAnalyticsCard>
    </LayoutTemplate>
  );
};

export default withRouter(CampaignAnalyticsSegments);
